import { FC, useState, useCallback, useEffect, useRef } from "react"
import {
  View,
  ViewStyle,
  TextStyle,
  Dimensions,
  TouchableOpacity,
  ScrollView,
  Alert,
  Linking,
  Image,
} from "react-native"
import { observer } from "mobx-react-lite"
import Animated, {
  FadeIn,
  FadeOut,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
  withSequence,
} from "react-native-reanimated"
import Icon from "react-native-vector-icons/MaterialIcons"

import { AddToCalendarButton } from "@/components/AddToCalendarButton.web"
import { AnimatedSyncingText } from "@/components/AnimatedDots"
import { pickerIntentForStatus } from "@/components/b2cReschedule/pickerIntent"
import { CelebrationBurst } from "@/components/CelebrationBurst"
import { OrderProgressBar } from "@/components/OrderProgressBar"
import { RatingSection } from "@/components/RatingSection"
import { Text } from "@/components/Text"
// Order not used from "@/models"
import { useStores } from "@/models"
import { downloadPDFReport } from "@/services/firebase"
import { type WeeklySchedule } from "@/services/firebase/functions"
import { colors, spacing } from "@/theme"
import { buildCalendarEvent, canExportToCalendar } from "@/utils/calendar-export"
import { getOpeningHoursLines } from "@/utils/formatOpeningHours"
import { formatETA } from "@/utils/formatters"
import { prefersReducedMotion } from "@/utils/reducedMotion"
import {
  coalesceProjectedTime,
  isLive as isOrderLive,
  selectPickupLabel,
  selectReturnLabel,
} from "@/utils/time-display"

const carImage = require("@assets/images/LCar.png")
const carImageDrive = require("@assets/images/LCar_Drive.png")
const carImageHub = require("@assets/images/LCar_Hub.png")

interface OrderBottomSheetProps {
  snapPoints?: string[]
  /**
   * Callback when bottom sheet height changes (for map bounds calculation)
   */
  onHeightChange?: (height: number) => void
  /**
   * When true, suppress bounce/expand animations (survey is still showing)
   */
  surveyActive?: boolean
}

const { width: SCREEN_WIDTH } = Dimensions.get("window")
const getCarDimensions = (isMobile: boolean = false, screenWidth: number = 1200) => {
  if (isMobile) {
    // For mobile: use responsive sizing based on screen width
    // Target 45% of screen width, with min/max constraints
    const targetWidth = Math.max(120, Math.min(320, screenWidth * 0.45))
    const targetHeight = targetWidth * 0.67 // Maintain aspect ratio

    return {
      width: targetWidth,
      height: targetHeight,
    }
  } else {
    // Desktop: keep existing logic
    const height = 160 * 1.3 // 208px
    const width = height / 0.67

    return {
      width,
      height,
    }
  }
}

export const OrderBottomSheet: FC<OrderBottomSheetProps> = observer(
  ({ snapPoints: providedSnapPoints, onHeightChange, surveyActive }) => {
    const { orderTrackingStore } = useStores()

    // Direct store access for MobX reactivity - MUST access in render
    const order = orderTrackingStore.currentOrder

    // Live ETA derived state. The "in N min" countdown was removed, so we no
    // longer need the 30s tick from useNow — liveActive + projected time are
    // pushed via Pusher / store updates.
    const liveActive = order ? isOrderLive(order) : false

    // Phase-aware pickup / return selectors (ADR-0012 §6).
    const pickupResult = order ? selectPickupLabel(order) : { iso: null, kind: "none" as const }
    const returnResult = order
      ? selectReturnLabel(order)
      : { iso: null, kind: "scheduled" as const }

    // Whether pickup has been realised — only used to drop the "~" approximation
    // marker on the pickup time once the handover is confirmed. The card POSITION
    // is stable (pickup left, return right) regardless of phase.
    const isPostPickup = pickupResult.kind === "realised_pickup"

    // Pickup card display values.
    const pickupDate = pickupResult.iso ? new Date(pickupResult.iso) : null
    // Drop the "~" approximation marker on realised pickups - it is an exact
    // timestamp, not a forecast. Keep "~" for projected / scheduled (pre-pickup).
    const pickupTimeText = pickupDate
      ? isPostPickup
        ? formatETA(pickupDate)
        : `~${formatETA(pickupDate)}`
      : "~--:--"
    const pickupDateText = pickupDate
      ? pickupDate.toLocaleDateString("de-DE", { day: "numeric", month: "short" })
      : "--"
    const pickupCardLabel =
      pickupResult.kind === "projected_door_arrival"
        ? "Abholung gegen"
        : pickupResult.kind === "realised_pickup"
          ? "Abgeholt um"
          : "Abholzeit"

    // Return card display values.
    const projectedReturnISO = order ? coalesceProjectedTime(order) : null
    const projectedReturnDate = projectedReturnISO ? new Date(projectedReturnISO) : null
    const returnLabel =
      returnResult.kind === "projected"
        ? liveActive
          ? "Rückgabezeit"
          : "Rückgabe gegen"
        : "Geplante Rückgabe"
    // Mobile cards are narrow (flex 0.4); "GEPLANTE RÜCKGABE" wraps to two lines
    // there. Abbreviate just for the mobile layout — desktop keeps the full phrase.
    const returnLabelMobile = returnLabel === "Geplante Rückgabe" ? "Gepl. Rückgabe" : returnLabel
    // Always prefix "~" — the projected return is a forecast, not a guarantee,
    // even when live. Honest-time principle.
    const returnTimeText = projectedReturnDate ? `~${formatETA(projectedReturnDate)}` : "~--:--"
    const returnDateText = projectedReturnDate
      ? projectedReturnDate.toLocaleDateString("de-DE", { day: "numeric", month: "short" })
      : "--"

    // Find opening hours for the hub location from allLocations
    const hubOpeningHours = order?.hubLocation?.name
      ? (orderTrackingStore.allLocations.find((l) => l.location === order.hubLocation?.name)
          ?.openingHours?.schedule as WeeklySchedule | undefined)
      : undefined

    // PLA-480 — calendar export. Anchored to scheduledPickupTime (the HubSpot
    // SLA ceiling), deliberately never to projectedPickupAtLive: a calendar
    // entry is a snapshot the customer never re-downloads, so it has to carry
    // the stable promise rather than a live projection that drifts. The venue
    // differs by service type — a driveby customer travels to the Prüfstelle,
    // an HBS customer stays home and a PA comes to them.
    const calendarOrder = order
      ? {
          id: order.id,
          serviceType: order.serviceType,
          status: order.status,
          scheduledPickupTime: order.scheduledPickupTime,
          hubLocation: order.hubLocation
            ? { name: order.hubLocation.name, address: order.hubLocation.address }
            : null,
          customerAddress: order.deliveryAddress?.formatted ?? null,
          updatedAt: order.updatedAt,
        }
      : null
    const calendarEvent =
      calendarOrder && canExportToCalendar(calendarOrder)
        ? buildCalendarEvent(
            calendarOrder,
            typeof window !== "undefined" ? window.location.href : undefined,
          )
        : null

    const [isExpanded, setIsExpanded] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const [inspectionReportAvailable, setInspectionReportAvailable] = useState(false)
    const [inspectionStatus, setInspectionStatus] = useState<
      "passed" | "failed" | "no-verdict" | null
    >(null)
    const [_hasSubmittedRating, setHasSubmittedRating] = useState(false)
    const [contentHeight, setContentHeight] = useState(300)
    const [headerHeight, setHeaderHeight] = useState(100)
    const [dynamicHeight, setDynamicHeight] = useState("35%")
    const [windowWidth, setWindowWidth] = useState(SCREEN_WIDTH)
    const isMobile = windowWidth < 1350
    const carDimensions = getCarDimensions(isMobile, windowWidth)
    const [showHelpContent, setShowHelpContent] = useState(false)
    const [isDragHandleHovered, setIsDragHandleHovered] = useState(false)

    // PLA-292 driveby completion confetti. HBS completion confetti lives on the
    // live map car; a driveby has no map car, so its celebration stays here on
    // the sheet. Driveby terminal = numericStatus === 2 (Order.ts collapses
    // ready_to_return/returning/completed). Fire once per order; the reset timer
    // is ref-held + cleared on unmount only so a dep re-run can't strand it true.
    const [drivebyCelebrate, setDrivebyCelebrate] = useState(false)
    const drivebyCelebratedRef = useRef<string | undefined>(undefined)
    const drivebyCelebrateTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    useEffect(() => {
      if (!order || !order.isDriveby) return
      if (order.numericStatus === 2 && drivebyCelebratedRef.current !== order.id) {
        drivebyCelebratedRef.current = order.id
        setDrivebyCelebrate(true)
        if (drivebyCelebrateTimerRef.current) clearTimeout(drivebyCelebrateTimerRef.current)
        drivebyCelebrateTimerRef.current = setTimeout(() => setDrivebyCelebrate(false), 1200)
      }
    }, [order?.isDriveby, order?.numericStatus, order?.id])
    useEffect(
      () => () => {
        if (drivebyCelebrateTimerRef.current) clearTimeout(drivebyCelebrateTimerRef.current)
      },
      [],
    )
    const [showInfoCard, setShowInfoCard] = useState(false)
    const [showNavPopup, setShowNavPopup] = useState(false)
    const hasClosedNavPopupRef = useRef(false)
    const scrollViewRef = useRef<ScrollView>(null)

    // Auto-scroll when help content is expanded
    useEffect(() => {
      if (showHelpContent && scrollViewRef.current) {
        // Small delay to ensure content has rendered
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true })
        }, 100)
      }
    }, [showHelpContent])

    const toggleExpanded = useCallback(() => {
      if (!order || surveyActive) return // Don't expand during loading or while survey is active
      setIsExpanded(!isExpanded)
    }, [isExpanded, order, surveyActive])

    // Handle window resize
    useEffect(() => {
      const handleResize = () => {
        const newWidth = Dimensions.get("window").width
        setWindowWidth(newWidth)
      }

      // Add event listener
      const subscription = Dimensions.addEventListener("change", handleResize)

      // Cleanup
      return () => {
        subscription?.remove()
      }
    }, [])

    // Animation for the handle arrow
    const translateY = useSharedValue(0)
    const [shouldAnimate, setShouldAnimate] = useState(false)
    const hasShownInfoCardRef = useRef(false)

    // Repeat animation every 30 seconds if not expanded
    useEffect(() => {
      if (isExpanded) {
        setShouldAnimate(false)
        setShowInfoCard(false)
        return
      }

      // Wait for order to load and survey to finish before showing reminder
      if (!order || surveyActive) {
        return
      }

      // Initial animation - start sooner on mobile for better discovery
      const initialDelay = isMobile ? 2000 : 5000 // 2s on mobile, 5s on desktop
      const initialTimer = setTimeout(() => {
        if (!isExpanded) {
          setShouldAnimate(true)
          // Show info card only on first animation ever (suppress for driveby Phase 1 — nav popup takes priority)
          if (!hasShownInfoCardRef.current && !(order?.isDriveby && order?.numericStatus === 1)) {
            setShowInfoCard(true)
            hasShownInfoCardRef.current = true
            // Hide info card after 4 seconds
            setTimeout(() => setShowInfoCard(false), 4000)
          }
          // Stop animation after duration (longer on mobile for visibility)
          const animationDuration = isMobile ? 7000 : 5000
          setTimeout(() => setShouldAnimate(false), animationDuration)
        }
      }, initialDelay)

      // Repeat animation - more frequently on mobile for better discovery
      const repeatInterval = isMobile ? 20000 : 30000 // 20s on mobile, 30s on desktop
      const intervalTimer = setInterval(() => {
        if (!isExpanded) {
          setShouldAnimate(true)
          // Stop after animation duration
          const animationDuration = isMobile ? 7000 : 5000
          setTimeout(() => setShouldAnimate(false), animationDuration)
        }
      }, repeatInterval)

      return () => {
        clearTimeout(initialTimer)
        clearInterval(intervalTimer)
        setShowInfoCard(false)
      }
    }, [isExpanded, isMobile, order, surveyActive])

    useEffect(() => {
      if (isDragHandleHovered && !isExpanded) {
        // Pull up when hovering
        translateY.value = withTiming(-5, { duration: 200 })
      } else if (!isExpanded && shouldAnimate) {
        // Start bouncing animation - more prominent on mobile
        const bounceHeight = isMobile ? -8 : -5 // Bigger bounce on mobile
        const bounceDuration = isMobile ? 700 : 600 // Slightly slower on mobile for visibility
        translateY.value = withRepeat(
          withSequence(
            withTiming(bounceHeight, { duration: bounceDuration }),
            withTiming(0, { duration: bounceDuration }),
          ),
          -1, // Infinite repeat
          true, // Reverse
        )
      } else {
        // Stop animation when expanded or not hovering
        translateY.value = withTiming(0, { duration: 200 })
      }
    }, [isExpanded, shouldAnimate, isDragHandleHovered, translateY, isMobile])

    const animatedArrowStyle = useAnimatedStyle(() => {
      return {
        transform: [{ translateY: translateY.value }],
      }
    })

    const getPhaseLabel = () => {
      // If no order yet but we're syncing/retrying, show success message
      // This prevents showing "Lade..." while waiting for order to be created
      if (!order && orderTrackingStore.isSyncing) {
        return "Termin bestätigt"
      }
      return order?.phaseLabel || "Lade..."
    }

    // Render phase label with styled driver name if available
    const renderPhaseLabel = () => {
      if (orderTrackingStore.isSyncing && !order) {
        return (
          <AnimatedSyncingText
            style={isMobile ? { ...$statusText, ...$statusTextMobile } : $statusText}
            animationDuration={800}
          />
        )
      }

      // Check if we should show driver name
      if (order) {
        // Pickup phase with driver name
        if (order.status === "picking_up" && order.pickupDriverName) {
          return (
            <View style={$phaseLabelRow}>
              <Text style={[$statusText, isMobile && $statusTextMobile]} preset="heading">
                {"Dein Fahrer "}
              </Text>
              <Icon
                name="person"
                size={isMobile ? 22 : 24}
                color={colors.primary}
                style={{ marginRight: 4, marginBottom: isMobile ? spacing.xs : 0 }}
              />
              <Text
                preset="heading"
                style={[$statusText, isMobile && $statusTextMobile, { color: colors.primary }]}
              >
                {order.pickupDriverName}
              </Text>
              <Text style={[$statusText, isMobile && $statusTextMobile]} preset="heading">
                {" ist unterwegs"}
              </Text>
            </View>
          )
        }

        // Return phase with driver name
        if (order.status === "returning" && order.returnDriverName) {
          return (
            <View style={$phaseLabelRow}>
              <Text style={[$statusText, isMobile && $statusTextMobile]} preset="heading">
                {"Dein Fahrer "}
              </Text>
              <Icon
                name="person"
                size={isMobile ? 22 : 24}
                color={colors.primary}
                style={{ marginRight: 4, marginBottom: isMobile ? spacing.xs : 0 }}
              />
              <Text
                preset="heading"
                style={[$statusText, isMobile && $statusTextMobile, { color: colors.primary }]}
              >
                {order.returnDriverName}
              </Text>
              <Text style={[$statusText, isMobile && $statusTextMobile]} preset="heading">
                {" ist unterwegs zu dir"}
              </Text>
            </View>
          )
        }
      }

      // Default: use standard phase label
      return (
        <Text
          style={[$statusText, isMobile && $statusTextMobile]}
          text={getPhaseLabel()}
          preset="heading"
        />
      )
    }

    // Select car image based on order status and service type
    const getCarImage = () => {
      if (!order) return carImage // Default: LCar.png

      // Special handling for PICKUP_DELIVERY service type
      if (order.isDriveby === false) {
        // Phases 4-5 (at_hub, ready_to_return): show hub car
        if (order.numericStatus === 4 || order.numericStatus === 5) {
          return carImageHub // LCar_Hub.png
        }
        // Phases 3 & 6 (picking_up, returning): show driving car
        if (order.numericStatus === 3 || order.numericStatus === 6) {
          return carImageDrive // LCar_Drive.png
        }
      }

      // Default for phases 1, 2, 7 and all DRIVEBY phases
      return carImage // LCar.png
    }

    // Set inspection report availability based on order data
    useEffect(() => {
      if (order) {
        // Use real data from backend
        setInspectionReportAvailable(order.hasPdfReport)

        // Set inspection status based on inspectionPassed field
        if (order.inspectionPassed === true) {
          setInspectionStatus("passed")
        } else if (order.inspectionPassed === false) {
          setInspectionStatus("failed")
        } else {
          setInspectionStatus("no-verdict")
        }
      }
    }, [order?.hasPdfReport, order?.inspectionPassed])

    // Auto-expand when order reaches step 5 or 6 (inspection report available)
    useEffect(() => {
      if (order?.isDriveby && order?.numericStatus === 2) {
        // For driveby phase 2, expand to show rating
        setIsExpanded(true)
      } else if (order?.numericStatus === 7) {
        // For phase 7 (completed), always expand to show rating
        setIsExpanded(true)
      } else if (order?.numericStatus === 5) {
        // Expand to show inspection report
        setIsExpanded(true)
      }
    }, [order?.numericStatus, order?.isDriveby])

    // Auto-show navigation popup for driveby orders in Phase 1
    useEffect(() => {
      if (
        order?.isDriveby &&
        order?.numericStatus === 1 &&
        order?.status !== "cancelled" &&
        order?.status !== "no_show" &&
        order?.hubLocation?.latitude &&
        order?.hubLocation?.longitude &&
        !hasClosedNavPopupRef.current
      ) {
        const timer = setTimeout(() => {
          setShowNavPopup(true)
        }, 1500)
        return () => clearTimeout(timer)
      } else {
        setShowNavPopup(false)
      }
      return undefined
    }, [
      order?.isDriveby,
      order?.numericStatus,
      order?.status,
      order?.hubLocation?.latitude,
      order?.hubLocation?.longitude,
    ])

    // Calculate dynamic height based on content and expansion state
    useEffect(() => {
      // Compact mode: no order loaded yet — show only drag handle + header
      // Height = measured header + drag handle (~52px) + footer (~36px desktop, ~24px mobile) + padding
      if (!order) {
        const dragHandleHeight = 52
        const footerHeight = isMobile ? 24 : 36
        const compactPadding = isMobile ? 12 : 16
        const compactHeight = headerHeight + dragHandleHeight + footerHeight + compactPadding
        setDynamicHeight(`${Math.max(compactHeight, 120)}px`)
        return
      }

      if (providedSnapPoints) {
        const minHeight = providedSnapPoints[0]
        const maxHeight = providedSnapPoints[1] || "70%"
        setDynamicHeight(isExpanded ? maxHeight : minHeight)
      } else {
        // Calculate based on content
        const screenHeight = Dimensions.get("window").height
        const minPercentageHeight = screenHeight * 0.2 // 20% minimum

        // For collapsed state, we need space for: handle + header (status + progress bar) + main content row
        // If content height is measured and larger than 20%, use that instead
        const paddingSize = isMobile ? 100 : 80 // More padding on mobile
        const totalContentHeight = contentHeight + headerHeight + paddingSize // Content + header + padding for larger arrow handle
        const minHeight = Math.max(
          minPercentageHeight,
          Math.min(totalContentHeight, screenHeight * 0.5),
        ) // Cap at 50% for collapsed

        // Height calculations for responsive design

        if (isExpanded) {
          // Calculate expanded height - fixed 75% for consistent behavior
          const expandedHeight = screenHeight * 0.75 // Always 75% when expanded
          setDynamicHeight(`${expandedHeight}px`)
        } else {
          setDynamicHeight(`${minHeight}px`)
        }
      }
    }, [isExpanded, contentHeight, headerHeight, isMobile, providedSnapPoints, order])

    // Report height changes to parent for map bounds calculation
    useEffect(() => {
      if (onHeightChange && dynamicHeight) {
        const screenHeight = Dimensions.get("window").height
        let heightInPixels = 0

        if (typeof dynamicHeight === "string") {
          if (dynamicHeight.endsWith("%")) {
            // Convert percentage to pixels
            const percentage = parseFloat(dynamicHeight) / 100
            heightInPixels = screenHeight * percentage
          } else if (dynamicHeight.endsWith("px")) {
            // Already in pixels
            heightInPixels = parseFloat(dynamicHeight)
          }
        }

        if (heightInPixels > 0) {
          onHeightChange(heightInPixels)
        }
      }
    }, [dynamicHeight, onHeightChange])

    // Measure content height
    const handleContentLayout = useCallback(
      (event: any) => {
        const { height } = event.nativeEvent.layout
        if (height > 0 && Math.abs(height - contentHeight) > 10) {
          setContentHeight(height)
        }
      },
      [contentHeight],
    )

    // Measure header height
    const handleHeaderLayout = useCallback(
      (event: any) => {
        const { height } = event.nativeEvent.layout
        if (height > 0 && Math.abs(height - headerHeight) > 5) {
          setHeaderHeight(height)
        }
      },
      [headerHeight],
    )

    const handleRatingSubmit = (_rating: number) => {
      // Rating submitted for order
      setHasSubmittedRating(true)
      // In a real app, you would send this rating to your backend
    }

    // Check if more than 24 hours have passed since pickup
    const isAfter24Hours =
      order?.pickupTime && Date.now() - new Date(order.pickupTime).getTime() > 24 * 60 * 60 * 1000

    const shouldRequestReport = !order?.hasPdfReport && isAfter24Hours

    const handleDownloadPDF = async () => {
      if (!order || !orderTrackingStore.currentOrderId || !orderTrackingStore.trackingToken) {
        Alert.alert("Fehler", "Berichtsinformationen nicht verfügbar")
        return
      }

      setIsDownloading(true)
      try {
        const result = await downloadPDFReport(
          orderTrackingStore.currentOrderId,
          orderTrackingStore.trackingToken,
        )

        // Open the PDF URL in the default browser/PDF viewer
        const supported = await Linking.canOpenURL(result.url)
        if (supported) {
          await Linking.openURL(result.url)
        } else {
          Alert.alert("Fehler", "PDF konnte nicht geöffnet werden")
        }
      } catch (error: any) {
        Alert.alert("Download fehlgeschlagen", error.message || "Unbekannter Fehler")
      } finally {
        setIsDownloading(false)
      }
    }

    const handleDismissNavPopup = useCallback(() => {
      setShowNavPopup(false)
      hasClosedNavPopupRef.current = true
    }, [])

    // Use mapsUrl from Firestore (curated business link) or fall back to Google Maps search
    const navUrl = order?.hubLocation
      ? order.hubLocation.mapsUrl ||
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("HolDeinePlakette " + order.hubLocation.address)}`
      : null

    const handleReportAction = async () => {
      if (shouldRequestReport) {
        // Redirect to KÜS website for report request
        const url = "https://www.kues-fahrzeugueberwachung.de/berichtskopien/"
        const supported = await Linking.canOpenURL(url)
        if (supported) {
          await Linking.openURL(url)
        } else {
          Alert.alert("Fehler", "Webseite konnte nicht geöffnet werden")
        }
      } else {
        // Existing download logic
        await handleDownloadPDF()
      }
    }

    return (
      <>
        {/* Navigation Popup for Driveby Phase 1 */}
        {showNavPopup && order?.isDriveby && order?.hubLocation && (
          <Animated.View
            entering={FadeIn.duration(500)}
            exiting={FadeOut.duration(300)}
            style={{
              position: "absolute",
              bottom: dynamicHeight as any,
              left: "50%",
              // @ts-ignore - Web-specific
              transform: "translateX(-50%)",
              marginBottom: isMobile ? 60 : 20,
              zIndex: 1000,
              width: isMobile ? "92%" : 380,
              maxWidth: 420,
            }}
          >
            <View style={$navPopupCard}>
              {/* Red header banner */}
              <View style={$navPopupHeader}>
                <View style={$navPopupHeaderContent}>
                  <Icon name="place" size={isMobile ? 20 : 22} color={colors.surface} />
                  <Text style={$navPopupHeaderTitle}>So kommst du zur Prüfstelle</Text>
                </View>
                <TouchableOpacity
                  onPress={handleDismissNavPopup}
                  style={$navPopupCloseButton}
                  // @ts-ignore - Web-specific
                  title="Schließen"
                >
                  <Icon name="close" size={16} color="rgba(255,255,255,0.8)" />
                </TouchableOpacity>
              </View>

              {/* Body */}
              <View style={[$navPopupBody, isMobile && { padding: spacing.sm }]}>
                {/* Hub info */}
                <View style={$navPopupHubRow}>
                  <View style={$navPopupHubDot} />
                  <View style={{ flex: 1 }}>
                    <Text style={$navPopupHubName}>{order.hubLocation.name}</Text>
                    <Text style={$navPopupAddress}>{order.hubLocation.address}</Text>
                  </View>
                </View>

                {/* Exclusivity note */}
                <View style={$navPopupNoteBox}>
                  <Icon
                    name="info-outline"
                    size={15}
                    color={colors.primary}
                    style={{ marginTop: 1 }}
                  />
                  <Text style={$navPopupNote}>
                    Wichtig: Wir haben deinen Termin reserviert & freuen uns auf dich {"\u2013 "}
                    deine Reservierung gilt nur hier, nicht an anderen Prüfstellen (z.B. TÜV Süd
                    oder anderen KÜS Prüfstellen).
                  </Text>
                </View>

                {/* Google Maps Button */}
                <TouchableOpacity
                  style={$navPopupButton}
                  onPress={() => {
                    if (navUrl && typeof window !== "undefined") {
                      window.open(navUrl, "_blank")
                    }
                  }}
                  // @ts-ignore - Web-specific
                  onMouseEnter={(e: any) => {
                    e.currentTarget.style.transform = "translateY(-1px)"
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(255, 56, 47, 0.45)"
                  }}
                  onMouseLeave={(e: any) => {
                    e.currentTarget.style.transform = "translateY(0)"
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(255, 56, 47, 0.3)"
                  }}
                >
                  <Icon name="navigation" size={18} color={colors.surface} />
                  <Text style={$navPopupButtonText}>In Maps öffnen</Text>
                  <Icon name="open-in-new" size={14} color="rgba(255,255,255,0.7)" />
                </TouchableOpacity>
              </View>
            </View>
          </Animated.View>
        )}

        {/* Info Card - Glass Morphism Style */}
        {showInfoCard && !isExpanded && (
          <Animated.View
            entering={FadeIn.duration(300)}
            exiting={FadeOut.duration(300)}
            style={{
              position: "absolute",
              bottom: dynamicHeight as any,
              left: "50%",
              // @ts-ignore - Web-specific
              transform: "translateX(-50%)",
              marginBottom: 16,
              zIndex: 999,
              width: isMobile ? "80%" : undefined,
            }}
          >
            <View
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                // @ts-ignore - Web-specific
                backdropFilter: "blur(10px)",
                borderRadius: 16,
                paddingVertical: 14,
                paddingHorizontal: 20,
                borderWidth: 1,
                borderColor: "rgba(255, 255, 255, 0.3)",
                // @ts-ignore - Web-specific
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                elevation: 5,
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
              }}
            >
              {/* Red accent line on left */}
              <View
                style={{
                  position: "absolute",
                  left: 0,
                  top: "20%",
                  bottom: "20%",
                  width: 3,
                  backgroundColor: colors.primary,
                  borderRadius: 2,
                }}
              />
              {/* Info icon */}
              <Icon name="info" size={20} color={colors.primary} style={{ marginLeft: 8 }} />
              {/* Text */}
              <Text
                style={{
                  color: colors.background,
                  fontSize: 15,
                  fontWeight: "600",
                  letterSpacing: 0.2,
                }}
              >
                Aufklappen um deine Buchung zu verwalten
              </Text>
            </View>
          </Animated.View>
        )}

        {/* Bottom Sheet */}
        <View
          // @ts-ignore - Web-specific data attribute for map bounds calculation
          data-bottom-sheet="true"
          style={[$webBottomSheet, { height: dynamicHeight as any }]}
        >
          {/* Drag Handle. Wrapped in a relative row so the calendar button can
              sit in the same bar as a SIBLING of the touchable — nesting it
              would put its taps inside the expand/collapse hit area. */}
          <View style={$dragHandleRow}>
            <TouchableOpacity
              onPress={toggleExpanded}
              style={[
                $dragHandleContainer,
                isDragHandleHovered && { backgroundColor: colors.palette.neutral200 },
              ]}
              // @ts-ignore - Web-specific properties
              onMouseEnter={() => setIsDragHandleHovered(true)}
              onMouseLeave={() => setIsDragHandleHovered(false)}
              title="Klicken zum Erweitern/Minimieren"
            >
              {!isExpanded ? (
                order ? (
                  <Animated.View
                    style={[
                      animatedArrowStyle,
                      {
                        // @ts-ignore - Web-specific
                        filter: isDragHandleHovered
                          ? "drop-shadow(0 0 20px rgba(255, 56, 47, 1))"
                          : "none",
                        // @ts-ignore - Web-specific
                        transition: "filter 0.3s ease",
                        // Ensure arrow is visible on mobile
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    ]}
                  >
                    <Icon
                      name="expand-less"
                      size={36}
                      color={
                        shouldAnimate || isMobile // Add mobile color
                          ? colors.primary
                          : isDragHandleHovered
                            ? colors.primary
                            : colors.palette.neutral400
                      }
                      style={
                        {
                          // @ts-ignore - Web-specific
                          transition: "color 0.3s ease",
                          // Ensure icon is visible
                          opacity: 1,
                        } as any
                      }
                    />
                  </Animated.View>
                ) : (
                  <View style={$handleIndicator} />
                )
              ) : (
                <View style={$handleIndicator} />
              )}
              {/* Web-only tooltip overlay */}
              {isDragHandleHovered && !isExpanded && (
                <View
                  // @ts-ignore
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    // @ts-ignore - Web-specific
                    color: colors.surface,
                    padding: "8px 12px",
                    borderRadius: 6,
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    marginTop: 8,
                    pointerEvents: "none",
                    zIndex: 1000,
                  }}
                >
                  <Text style={{ color: colors.surface, fontSize: 14 }}>Klicken zum Erweitern</Text>
                </View>
              )}
            </TouchableOpacity>

            {/* PLA-480 — "Zum Kalender hinzufügen", right-aligned in the drag
                bar. Collapsed only: expanding swaps the 36px expand arrow for
                the 4px handle indicator, shrinking this bar from 52px to 20px,
                which left the button clipped by the sheet's rounded top edge.
                The expanded sheet shows the full address block anyway, so the
                affordance is not lost — it returns on collapse. */}
            {calendarEvent && !isExpanded && (
              <View style={$calendarButtonAnchor}>
                <AddToCalendarButton event={calendarEvent} isMobile={isMobile} />
              </View>
            )}
          </View>

          {/* Sticky Header */}
          <View
            style={[
              $stickyHeader,
              {
                paddingHorizontal: windowWidth < 768 ? spacing.sm : spacing.md,
                paddingTop: windowWidth < 768 ? 0 : spacing.xs,
              },
            ]}
            onLayout={handleHeaderLayout}
          >
            {/* Current Status Display */}
            {renderPhaseLabel()}

            {/* Progress Bar - Always Visible */}
            <OrderProgressBar
              currentPhase={order?.numericStatus || 1}
              style={isMobile ? { ...$progressBar, ...$progressBarMobile } : $progressBar}
              isDriveby={order?.isDriveby}
            />
          </View>

          <ScrollView
            ref={scrollViewRef}
            style={$content}
            contentContainerStyle={[
              $scrollContentContainer,
              {
                paddingHorizontal: windowWidth < 768 ? spacing.sm : spacing.md,
                paddingBottom: windowWidth < 768 ? spacing.md : spacing.lg,
              },
            ]}
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            {/* Rating Section for Phase 7 (or Phase 2 for driveby) - Directly under progress bar */}
            {((order?.numericStatus === 7 && !order?.isDriveby) ||
              (order?.numericStatus === 2 && order?.isDriveby)) && (
              <RatingSection
                onRatingSubmit={handleRatingSubmit}
                style={{
                  marginBottom: spacing.md,
                }}
              />
            )}

            <View>
              {/* Collapsed Content - Only shown when order data exists */}
              {order && (
                <View onLayout={handleContentLayout}>
                  {/* Mobile vs Desktop Layout */}
                  {isMobile ? (
                    /* Mobile Layout: Car on left, Time cards stacked on right */
                    <View style={isExpanded ? $mobileExpandedWrapper : $mobileCenteredWrapper}>
                      {/* Main Row: Car (left) + Time cards (right) */}
                      <View style={$mobileMainRow}>
                        {/* Car Image - Left side */}
                        <View style={$mobileCarContainer}>
                          <Image
                            source={getCarImage()}
                            style={{
                              width: carDimensions.width,
                              height: carDimensions.height,
                            }}
                            resizeMode="contain"
                          />
                        </View>

                        {/* Time Cards - Right side (stacked vertically) */}
                        <View style={$mobileTimeCardsColumn}>
                          {order?.isDriveby ? (
                            /* Driveby: Only show appointment time with date (no pickup leg) */
                            <View style={$mobileTimeCard}>
                              <Text
                                style={$mobileTimeCardLabel}
                                text="Uhrzeit Termin"
                                preset="formLabel"
                                numberOfLines={1}
                              />
                              <Text
                                style={$mobileTimeCardTime}
                                text={
                                  order?.pickupTime ? `~${formatETA(order.pickupTime)}` : "~--:--"
                                }
                                preset="heading"
                              />
                              <Text
                                style={$mobileTimeCardDate}
                                text={
                                  order?.pickupTime
                                    ? new Date(order.pickupTime).toLocaleDateString("de-DE", {
                                        day: "numeric",
                                        month: "short",
                                      })
                                    : "--"
                                }
                                preset="formLabel"
                              />
                            </View>
                          ) : (
                            /* HBS: stable layout — pickup card always left, return always right.
                               The pickup card's label/value swap to reflect realised state via
                               pickupCardLabel ("Abgeholt um") and pickupTimeText (drops the "~"
                               approximation marker). The card position itself does NOT swap. */
                            <>
                              {/* Pickup: Abholzeit / Abholung gegen / Abgeholt um */}
                              <View style={$mobileTimeCard}>
                                <Text
                                  style={$mobileTimeCardLabel}
                                  text={pickupCardLabel}
                                  preset="formLabel"
                                  numberOfLines={1}
                                />
                                <Text
                                  style={$mobileTimeCardTime}
                                  text={pickupTimeText}
                                  preset="heading"
                                />
                              </View>

                              {/* Return: Rückgabezeit */}
                              <View style={$mobileTimeCard}>
                                <Text
                                  style={$mobileTimeCardLabel}
                                  text={returnLabelMobile}
                                  preset="formLabel"
                                  numberOfLines={1}
                                />
                                <Text
                                  style={$mobileTimeCardTime}
                                  text={returnTimeText}
                                  preset="heading"
                                />
                              </View>
                            </>
                          )}
                        </View>
                      </View>
                    </View>
                  ) : (
                    /* Desktop Layout: Original design */
                    <View
                      style={[
                        $centeredContentContainer,
                        isExpanded && $centeredContentContainerExpanded,
                      ]}
                    >
                      <View style={[$mainContentRow, isMobile && $mainContentRowMobile]}>
                        {/* Car Image - Left Side */}
                        <View style={[$carContainer, isMobile && $carContainerMobile]}>
                          <Image
                            source={getCarImage()}
                            style={{
                              width: carDimensions.width,
                              height: carDimensions.height,
                            }}
                            resizeMode="contain"
                          />
                        </View>

                        {/* Cards Section - Center */}
                        <View style={[$cardsSection, isMobile && $cardsSectionMobile]}>
                          {/* Time Cards Container */}
                          <View
                            style={[$timeCardsContainer, isMobile && $timeCardsContainerMobile]}
                          >
                            {order?.isDriveby ? (
                              /* Driveby: Only show appointment time (no pickup leg — ADR-0012 §7) */
                              <View style={[$uniformCard, isMobile && $uniformCardMobile]}>
                                <Text
                                  style={[$cardLabel, isMobile && $cardLabelMobile]}
                                  text="Uhrzeit Termin"
                                  preset="formLabel"
                                />
                                <Text
                                  style={[$cardTime, isMobile && $cardTimeMobile]}
                                  text={
                                    order?.pickupTime ? `~${formatETA(order.pickupTime)}` : "~--:--"
                                  }
                                  preset="heading"
                                />
                                <Text
                                  style={[$cardDate, isMobile && $cardDateMobile]}
                                  text={
                                    order?.pickupTime
                                      ? new Date(order.pickupTime).toLocaleDateString("de-DE", {
                                          day: "numeric",
                                          month: "short",
                                        })
                                      : "--"
                                  }
                                  preset="formLabel"
                                />
                              </View>
                            ) : (
                              /* HBS: stable layout — pickup card always left, return always right.
                                 The pickup card's label/value swap to reflect realised state via
                                 pickupCardLabel ("Abgeholt um") and pickupTimeText (drops the "~"
                                 approximation marker). The card position itself does NOT swap. */
                              <>
                                {/* Pickup: Abholzeit / Abholung gegen / Abgeholt um */}
                                <View style={[$uniformCard, isMobile && $uniformCardMobile]}>
                                  <Text
                                    style={[$cardLabel, isMobile && $cardLabelMobile]}
                                    text={pickupCardLabel}
                                    preset="formLabel"
                                  />
                                  <Text
                                    style={[$cardTime, isMobile && $cardTimeMobile]}
                                    text={pickupTimeText}
                                    preset="heading"
                                  />
                                  <Text
                                    style={[$cardDate, isMobile && $cardDateMobile]}
                                    text={pickupDateText}
                                    preset="formLabel"
                                  />
                                </View>

                                {/* Separator */}
                                <View style={[$separatorLine, isMobile && $separatorLineMobile]} />

                                {/* Return: Rückgabe gegen / Geplante Rückgabe */}
                                <View style={[$uniformCard, isMobile && $uniformCardMobile]}>
                                  <Text
                                    style={[$cardLabel, isMobile && $cardLabelMobile]}
                                    text={returnLabel}
                                    preset="formLabel"
                                  />
                                  <Text
                                    style={[$cardTime, isMobile && $cardTimeMobile]}
                                    text={returnTimeText}
                                    preset="heading"
                                  />
                                  <Text
                                    style={[$cardDate, isMobile && $cardDateMobile]}
                                    text={returnDateText}
                                    preset="formLabel"
                                  />
                                </View>
                              </>
                            )}
                          </View>
                        </View>

                        {/* Inspection Report - Right Side */}
                        <View
                          style={[$inspectionContainer, isMobile && $inspectionContainerMobile]}
                        >
                          <TouchableOpacity
                            style={[
                              $inspectionCard,
                              isMobile && $inspectionCardMobile,
                              !inspectionReportAvailable &&
                                !shouldRequestReport &&
                                $inspectionCardDisabled,
                            ]}
                            disabled={
                              (!order?.hasPdfReport && !shouldRequestReport) || isDownloading
                            }
                            onPress={handleReportAction}
                          >
                            {/* Status Indicator - Top Right */}
                            <View style={$statusIndicator}>
                              {inspectionStatus === "passed" && (
                                <View
                                  style={[$statusButton, { backgroundColor: colors.statusPassed }]}
                                >
                                  <Text style={$statusIcon}>✓</Text>
                                </View>
                              )}
                              {inspectionStatus === "failed" && (
                                <View
                                  style={[$statusButton, { backgroundColor: colors.statusFailed }]}
                                >
                                  <Text style={$statusIcon}>✗</Text>
                                </View>
                              )}
                              {(inspectionStatus === "no-verdict" || inspectionStatus === null) && (
                                <View
                                  style={[$statusButton, { backgroundColor: colors.statusPending }]}
                                >
                                  <Icon name="hourglass-empty" size={10} color={colors.surface} />
                                </View>
                              )}
                            </View>

                            {/* Card Content - Same structure as time cards */}
                            <Text style={[$cardLabel, isMobile && $cardLabelMobile]}>
                              {isDownloading
                                ? "Lädt..."
                                : shouldRequestReport
                                  ? "Bericht anfordern"
                                  : "Prüfbericht"}
                            </Text>
                            <View style={$inspectionIconContainer}>
                              <Icon
                                name={
                                  isDownloading
                                    ? "hourglass-empty"
                                    : shouldRequestReport
                                      ? "open-in-new"
                                      : "file-download"
                                }
                                size={28}
                                color={colors.surface}
                              />
                            </View>
                            {inspectionStatus && (
                              <Text style={[$cardDate, isMobile && $cardDateMobile]}>
                                {inspectionStatus === "passed" && "Bestanden"}
                                {inspectionStatus === "failed" && "Nicht bestanden"}
                                {inspectionStatus === "no-verdict" && "Bald verfügbar"}
                              </Text>
                            )}
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  )}
                </View>
              )}
              {/* End of Collapsed Content Measurement */}

              {/* Expanded Content - Not measured for collapsed height */}
              {isExpanded && order && (
                <Animated.View entering={FadeIn} exiting={FadeOut} style={$expandedContent}>
                  {/* Mobile Prüfbericht - Only shown when expanded */}
                  {isMobile &&
                    order &&
                    (order.numericStatus === 4 ||
                      order.numericStatus === 5 ||
                      order.numericStatus === 6 ||
                      order.numericStatus === 7 ||
                      (order.isDriveby &&
                        (order.numericStatus === 1 || order.numericStatus === 2))) && (
                      <View style={$mobileExpandedPrufberichtContainer}>
                        <TouchableOpacity
                          style={[
                            $mobileFullWidthInspectionCard,
                            !inspectionReportAvailable &&
                              !shouldRequestReport &&
                              $inspectionButtonDisabled,
                          ]}
                          disabled={(!order?.hasPdfReport && !shouldRequestReport) || isDownloading}
                          onPress={handleReportAction}
                        >
                          {/* Left side: Label and status */}
                          <View style={$mobileInspectionLeftSide}>
                            <Text style={$mobileInspectionLabel}>
                              {isDownloading
                                ? "Lädt..."
                                : shouldRequestReport
                                  ? "Bericht anfordern"
                                  : "Prüfbericht"}
                            </Text>
                            {inspectionStatus && (
                              <Text style={$mobileInspectionStatus}>
                                {inspectionStatus === "passed" && "Bestanden"}
                                {inspectionStatus === "failed" && "Nicht bestanden"}
                                {inspectionStatus === "no-verdict" &&
                                  "Öffne den Bericht, sobald er verfügbar ist."}
                              </Text>
                            )}
                          </View>

                          {/* Right side: Status indicator and download button */}
                          <View style={$mobileInspectionRightSide}>
                            {/* Status Indicator */}
                            <View style={$mobileInlineStatusIndicator}>
                              {inspectionStatus === "passed" && (
                                <View
                                  style={[$statusButton, { backgroundColor: colors.statusPassed }]}
                                >
                                  <Text style={$statusIcon}>✓</Text>
                                </View>
                              )}
                              {inspectionStatus === "failed" && (
                                <View
                                  style={[$statusButton, { backgroundColor: colors.statusFailed }]}
                                >
                                  <Text style={$statusIcon}>✗</Text>
                                </View>
                              )}
                              {(inspectionStatus === "no-verdict" || inspectionStatus === null) && (
                                <View
                                  style={[$statusButton, { backgroundColor: colors.statusPending }]}
                                >
                                  <Icon name="hourglass-empty" size={10} color={colors.surface} />
                                </View>
                              )}
                            </View>
                            <View style={$mobileInspectionIconContainer}>
                              <Icon
                                name={
                                  isDownloading
                                    ? "hourglass-empty"
                                    : shouldRequestReport
                                      ? "open-in-new"
                                      : "file-download"
                                }
                                size={24}
                                color={colors.surface}
                              />
                            </View>
                          </View>
                        </TouchableOpacity>
                      </View>
                    )}

                  {/* Help Card - Shown after report section */}
                  <View style={$mobileExpandedPrufberichtContainer}>
                    <View style={$helpCard}>
                      <TouchableOpacity
                        style={$helpCardHeader}
                        onPress={() => setShowHelpContent(!showHelpContent)}
                      >
                        <Text style={$helpCardTitle}>Termin ändern oder Fragen?</Text>
                        <Icon
                          name={showHelpContent ? "expand-less" : "expand-more"}
                          size={24}
                          color={colors.text}
                        />
                      </TouchableOpacity>

                      {showHelpContent && (
                        <Animated.View entering={FadeIn} exiting={FadeOut} style={$helpCardContent}>
                          <Text style={$helpCardText}>
                            Wir sind für dich da!{"\n"}
                            <Text
                              style={$helpCardLink}
                              onPress={() => Linking.openURL("tel:09118808306")}
                            >
                              0911 880 8306
                            </Text>
                            {"\n"}
                            Mo-Fr: 8:00–18:00 Uhr{"\n"}
                            Sa: 8:00–12:00 Uhr
                          </Text>

                          <View
                            style={[
                              $buttonRow,
                              windowWidth < 768
                                ? { flexDirection: "column", gap: spacing.xs }
                                : { gap: spacing.sm },
                            ]}
                          >
                            {/* Top row on mobile: Verschieben and Stornieren */}
                            {windowWidth < 768 ? (
                              <>
                                <View style={{ flexDirection: "row", gap: spacing.xs }}>
                                  {(order?.orderSource?.startsWith("b2c-website") ||
                                    order?.ticketRescheduling) && (
                                    <TouchableOpacity
                                      style={[
                                        $rescheduleButtonInRow,
                                        {
                                          paddingVertical: spacing.xs,
                                          paddingHorizontal: spacing.sm,
                                        },
                                      ]}
                                      onPress={() =>
                                        orderTrackingStore.showB2cRescheduleModalAction()
                                      }
                                      activeOpacity={0.8}
                                    >
                                      <Icon name="event" size={18} color={colors.surface} />
                                      <Text
                                        style={[$rescheduleButtonText, { fontSize: 12 }]}
                                        numberOfLines={1}
                                        ellipsizeMode="tail"
                                      >
                                        {pickerIntentForStatus(order?.status, order?.orderSource)
                                          ? "Neuen Termin wählen"
                                          : "Verschieben"}
                                      </Text>
                                    </TouchableOpacity>
                                  )}
                                  {(order?.orderSource?.startsWith("b2c-website") ||
                                    order?.ticketCancellation) &&
                                    !pickerIntentForStatus(order?.status, order?.orderSource) && (
                                      <TouchableOpacity
                                        style={[
                                          $cancelButtonInRow,
                                          {
                                            paddingVertical: spacing.xs,
                                            paddingHorizontal: spacing.sm,
                                          },
                                        ]}
                                        onPress={() =>
                                          orderTrackingStore.showB2cCancelModalAction()
                                        }
                                        activeOpacity={0.8}
                                      >
                                        <Icon name="cancel" size={18} color={colors.surface} />
                                        <Text
                                          style={[$cancelButtonText, { fontSize: 12 }]}
                                          numberOfLines={1}
                                          ellipsizeMode="tail"
                                        >
                                          Stornieren
                                        </Text>
                                      </TouchableOpacity>
                                    )}
                                </View>
                                {/* Bottom row on mobile: WhatsApp full width */}
                                <TouchableOpacity
                                  style={[
                                    $whatsappButton,
                                    {
                                      paddingVertical: spacing.xs,
                                      paddingHorizontal: spacing.sm,
                                    },
                                  ]}
                                  onPress={() => Linking.openURL("https://wa.me/499118808306")}
                                >
                                  <Icon name="chat" size={18} color={colors.surface} />
                                  <Text
                                    style={[$whatsappButtonText, { fontSize: 12 }]}
                                    numberOfLines={1}
                                    ellipsizeMode="tail"
                                  >
                                    Whatsapp
                                  </Text>
                                </TouchableOpacity>
                              </>
                            ) : (
                              <>
                                {/* Desktop: All three in a row */}
                                {(order?.orderSource?.startsWith("b2c-website") ||
                                  order?.ticketRescheduling) && (
                                  <TouchableOpacity
                                    style={$rescheduleButtonInRow}
                                    onPress={() =>
                                      orderTrackingStore.showB2cRescheduleModalAction()
                                    }
                                    activeOpacity={0.8}
                                  >
                                    <Icon name="event" size={20} color={colors.surface} />
                                    <Text
                                      style={$rescheduleButtonText}
                                      numberOfLines={1}
                                      ellipsizeMode="tail"
                                    >
                                      {pickerIntentForStatus(order?.status, order?.orderSource)
                                        ? "Neuen Termin wählen"
                                        : "Verschieben"}
                                    </Text>
                                  </TouchableOpacity>
                                )}
                                {(order?.orderSource?.startsWith("b2c-website") ||
                                  order?.ticketCancellation) &&
                                  !pickerIntentForStatus(order?.status, order?.orderSource) && (
                                    <TouchableOpacity
                                      style={$cancelButtonInRow}
                                      onPress={() => orderTrackingStore.showB2cCancelModalAction()}
                                      activeOpacity={0.8}
                                    >
                                      <Icon name="cancel" size={20} color={colors.surface} />
                                      <Text
                                        style={$cancelButtonText}
                                        numberOfLines={1}
                                        ellipsizeMode="tail"
                                      >
                                        Stornieren
                                      </Text>
                                    </TouchableOpacity>
                                  )}
                                <TouchableOpacity
                                  style={$whatsappButton}
                                  onPress={() => Linking.openURL("https://wa.me/499118808306")}
                                >
                                  <Icon
                                    name="chat"
                                    size={20}
                                    color={colors.surface}
                                    style={{ marginRight: spacing.xs }}
                                  />
                                  <Text
                                    style={$whatsappButtonText}
                                    numberOfLines={1}
                                    ellipsizeMode="tail"
                                  >
                                    Whatsapp
                                  </Text>
                                </TouchableOpacity>
                              </>
                            )}
                          </View>
                        </Animated.View>
                      )}
                    </View>
                  </View>

                  {/* Prüfstellen Adresse */}
                  <View style={$addressSection}>
                    <Text style={$addressTitle}>Prüfstellen Adresse</Text>
                    <View style={$addressContainer}>
                      {/* Address row */}
                      <View style={$addressRow}>
                        <Icon
                          name="place"
                          size={18}
                          color={colors.primary}
                          style={{ marginTop: 2 }}
                        />
                        <Text style={$addressText}>
                          {order?.hubLocation?.address || "Adresse wird geladen..."}
                        </Text>
                      </View>

                      {/* Opening hours rows */}
                      {hubOpeningHours &&
                        getOpeningHoursLines(hubOpeningHours).map((line, i) => (
                          <View
                            key={i}
                            style={[$openingHoursRow, i === 0 && { marginTop: spacing.xs }]}
                          >
                            <Icon
                              name="schedule"
                              size={16}
                              color={i === 0 ? colors.textDim : "transparent"}
                              style={{ marginTop: 2 }}
                            />
                            <Text style={$openingHoursText}>{line}</Text>
                          </View>
                        ))}

                      {/* Separator + Maps link */}
                      <View style={$mapsSeparator} />
                      <TouchableOpacity
                        style={$mapsLinkRow}
                        onPress={() => {
                          if (!order?.hubLocation) return
                          const url =
                            order.hubLocation.mapsUrl ||
                            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("HolDeinePlakette " + order.hubLocation.address)}`
                          if (typeof window !== "undefined") {
                            window.open(url, "_blank")
                          }
                        }}
                      >
                        <Text style={$mapsLink}>In Maps öffnen</Text>
                        <Icon name="open-in-new" size={16} color={colors.primary} />
                      </TouchableOpacity>
                    </View>
                  </View>

                  {/* Abholadresse - Hide for driveby */}
                  {!order?.isDriveby && (
                    <View style={$addressSection}>
                      <Text style={$addressTitle}>Abholadresse</Text>
                      <View style={$addressContainer}>
                        <View style={$addressRow}>
                          <Icon
                            name="home"
                            size={18}
                            color={colors.textDim}
                            style={{ marginTop: 2 }}
                          />
                          <Text style={$addressText}>
                            {order?.deliveryAddress?.formatted || "Adresse wird geladen..."}
                          </Text>
                        </View>
                      </View>
                    </View>
                  )}
                </Animated.View>
              )}
            </View>
          </ScrollView>
        </View>

        {/* Driveby completion confetti (PLA-292). HBS confetti lives on the map
            car; a driveby has no map car, so its celebration stays on the sheet.
            Sibling of the sheet (outside its overflow:hidden) so the burst is not
            clipped. Reduced-motion → render nothing. */}
        {drivebyCelebrate && !prefersReducedMotion() && (
          <View pointerEvents="none" style={[$confettiOverlay, { height: dynamicHeight as any }]}>
            <View style={$confettiOverlayAnchor}>
              <CelebrationBurst />
            </View>
          </View>
        )}
      </>
    )
  },
)

// Web-specific bottom sheet styling
const $webBottomSheet: ViewStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: colors.surface,
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  // @ts-ignore - Web-specific
  boxShadow: `0 -2px 4px rgba(17, 30, 44, 0.1)`,
  elevation: 5,
  zIndex: 1000,
  overflow: "hidden", // Keep hidden for the container to maintain rounded corners
  // @ts-ignore - Web-specific transition property
  transition: "height 0.3s ease-out", // Smooth height transitions
}

// PLA-292: driveby confetti overlay — a sibling of $webBottomSheet (outside its
// overflow:hidden) so the burst fountains past the sheet's clip. Never intercepts touches.
const $confettiOverlay: ViewStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1001,
}

// 0×0 burst origin near the top of the sheet region (where the car + rating reveal sit).
const $confettiOverlayAnchor: ViewStyle = {
  position: "absolute",
  top: "22%",
  left: "50%",
  width: 0,
  height: 0,
}

/**
 * Positioning context for the calendar button so it can sit inside the drag bar
 * without being nested in the expand/collapse touchable (PLA-480).
 *
 * `zIndex` is load-bearing. react-native-web's base View style is
 * `position: relative; z-index: 0` (see its View/index.js), so EVERY View
 * already creates a stacking context — this row included. That traps the
 * calendar button's own `zIndex: 30` inside this row's context, where it can
 * never outrank $stickyHeader's `zIndex: 10`. The header paints an opaque
 * surface-coloured background, so the open popover rendered behind it.
 *
 * Lifting the row above the header fixes it for the whole subtree. Keep this
 * greater than $stickyHeader's zIndex.
 */
const $dragHandleRow: ViewStyle = {
  position: "relative",
  zIndex: 50,
}

const $dragHandleContainer: ViewStyle = {
  alignItems: "center",
  paddingVertical: spacing.xs, // Reduced from spacing.md (16px) to spacing.xs (8px)
  cursor: "pointer" as any,
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  // @ts-ignore - Web-specific
  transition: "background-color 0.2s ease",
}

const $handleIndicator: ViewStyle = {
  backgroundColor: colors.palette.neutral400,
  width: 40,
  height: 4,
  borderRadius: 2,
  opacity: 0.8,
}

const $stickyHeader: ViewStyle = {
  backgroundColor: colors.surface,
  zIndex: 10,
}

/**
 * Right-aligned in the drag bar, centred on its height.
 *
 * No height floor is needed because the button renders only when the sheet is
 * collapsed AND an order exists — and that is exactly the branch that draws the
 * 36px expand arrow, making the bar 8+36+8 = 52px. The 36px button centres at
 * 8..44, precisely the arrow's band, well clear of the sheet's clipped top edge.
 *
 * The other branches all draw the 4px handle indicator (20px bar), which is too
 * short to centre the button in — but they are unreachable here: expanded is
 * gated out at the call site, and the collapsed-without-order case has no
 * calendar event to render.
 */
const $calendarButtonAnchor: ViewStyle = {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: spacing.sm,
  justifyContent: "center",
  zIndex: 30,
}

const $content: ViewStyle = {
  flex: 1, // Take full available height
}

const $scrollContentContainer: ViewStyle = {
  paddingTop: spacing.xs,
  flexGrow: 1,
}

const $statusText: TextStyle = {
  color: colors.text,
  marginTop: 0,
  marginBottom: 0,
  textAlign: "center",
}

const $progressBar: ViewStyle = {
  marginBottom: 0,
  marginHorizontal: spacing.lg,
}

const $centeredContentContainer: ViewStyle = {
  flex: 1,
  justifyContent: "center",
  alignItems: "center",
}

const $centeredContentContainerExpanded: ViewStyle = {
  justifyContent: "flex-start",
  paddingTop: spacing.xl, // More padding when expanded to prevent overlap
}

// Main content layout
const $mainContentRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingHorizontal: spacing.sm,
  width: "100%", // Ensure it takes full width of centered container
}

const $mainContentRowMobile: ViewStyle = {
  flexDirection: "column",
  alignItems: "center",
  paddingHorizontal: 0,
}

const $carContainer: ViewStyle = {
  flex: 0.3,
  alignItems: "flex-start", // Align to the left
  paddingLeft: 0,
}

const $carContainerMobile: ViewStyle = {
  marginBottom: spacing.md,
  flex: 0,
}

// Cards section (center)
const $cardsSection: ViewStyle = {
  flex: 0.4,
  alignItems: "center",
  justifyContent: "center",
}

const $cardsSectionMobile: ViewStyle = {
  flex: 0,
  width: "100%",
  marginBottom: spacing.md,
}

const $timeCardsContainer: ViewStyle = {
  flexDirection: "row",
  // stretch (not center) so both cards always share the same height — when
  // the live Rückgabezeit card grows by one line ("gleich" / "in N min"),
  // the Abholzeit card grows to match instead of looking shorter.
  alignItems: "stretch",
  gap: spacing.md,
}

const $timeCardsContainerMobile: ViewStyle = {
  flexDirection: "column",
  gap: spacing.sm,
  width: "100%",
  alignItems: "center",
}

// Uniform card styling — refined with accent line and gradient
const $uniformCard: ViewStyle = {
  backgroundColor: colors.background,
  borderRadius: 20,
  paddingVertical: spacing.lg,
  paddingHorizontal: spacing.xl,
  paddingLeft: (spacing.xl as number) + 2,
  alignItems: "flex-start",
  justifyContent: "center",
  minWidth: 160,
  minHeight: 140,
  position: "relative",
  borderLeftWidth: 4,
  borderLeftColor: colors.primary,
  // @ts-ignore - Web-specific
  boxShadow: "0 4px 12px rgba(17, 30, 44, 0.18), inset 0 1px 0 rgba(255,255,255,0.04)",
  // @ts-ignore - Web-specific
  backgroundImage: `linear-gradient(135deg, ${colors.surfaceDarkTop} 0%, ${colors.surfaceDarkBottom} 100%)`,
}

const $uniformCardMobile: ViewStyle = {
  minWidth: 140,
  minHeight: 140,
  paddingHorizontal: spacing.md,
  paddingLeft: (spacing.md as number) + 2,
}

// Card text styles
const $cardLabel: TextStyle = {
  color: colors.surface,
  fontSize: 11,
  marginBottom: spacing.xs,
  opacity: 0.6,
  fontWeight: "600",
  letterSpacing: 1,
  textTransform: "uppercase",
}

const $cardLabelMobile: TextStyle = {
  fontSize: 10,
}

const $cardTime: TextStyle = {
  color: colors.surface,
  fontSize: 28,
  fontWeight: "700",
  marginBottom: spacing.xs,
  letterSpacing: -1,
}

const $cardTimeMobile: TextStyle = {
  fontSize: 22,
}

const $cardDate: TextStyle = {
  color: colors.surface,
  fontSize: 13,
  opacity: 0.5,
  fontWeight: "500",
}

const $cardDateMobile: TextStyle = {
  fontSize: 11,
}

// Modern separator line
const $separatorLine: ViewStyle = {
  width: 2,
  height: 60,
  alignSelf: "center", // stay vertically centred even when parent stretches
  backgroundColor: colors.palette.neutral300,
  opacity: 0.5,
}

const $separatorLineMobile: ViewStyle = {
  width: 100,
  height: 2,
}

// Inspection container (right side)
const $inspectionContainer: ViewStyle = {
  flex: 0.3,
  alignItems: "flex-end", // Align to the right
  paddingRight: 0,
}

const $inspectionContainerMobile: ViewStyle = {
  flex: 0,
  width: "100%",
  alignItems: "center",
}

// Mobile specific styles
const $statusTextMobile: TextStyle = {
  fontSize: 18,
  marginBottom: spacing.xs, // Reduce gap to progress bar
}

const $progressBarMobile: ViewStyle = {
  marginHorizontal: spacing.sm,
  marginTop: 0, // Remove any top margin
}

// const $syncingContainer: ViewStyle = {
//   alignItems: "center",
//   justifyContent: "center",
// }

// const $syncingTextContainer: ViewStyle = {
//   flexDirection: "row",
//   alignItems: "center",
//   justifyContent: "center",
// }

const $inspectionCard: ViewStyle = {
  backgroundColor: colors.background, // Same blue as time cards
  borderRadius: 20,
  paddingVertical: spacing.lg,
  paddingHorizontal: spacing.lg,
  alignItems: "center",
  justifyContent: "center",
  minWidth: 160, // Same as uniform cards
  height: 140, // Fixed height to match other cards exactly
  // @ts-ignore - Web-specific
  boxShadow: `0 4px 8px rgba(17, 30, 44, 0.15)`,
  elevation: 5,
  position: "relative", // Needed for absolute positioning of status indicator
}

const $inspectionCardDisabled: ViewStyle = {
  opacity: 0.5,
}

const $inspectionCardMobile: ViewStyle = {
  minWidth: 140,
  height: 140, // Fixed height on mobile to match other cards
  paddingHorizontal: spacing.md,
}

const $inspectionIconContainer: ViewStyle = {
  marginVertical: spacing.xs,
}

// Status indicator styles
const $statusIndicator: ViewStyle = {
  position: "absolute",
  top: spacing.sm,
  right: spacing.sm,
  zIndex: 1,
}

const $statusButton: ViewStyle = {
  width: 28,
  height: 28,
  borderRadius: 14,
  justifyContent: "center",
  alignItems: "center",
  // @ts-ignore - Web-specific
  boxShadow: "0 2px 3px rgba(0, 0, 0, 0.2)",
  elevation: 3,
}

const $statusIcon: TextStyle = {
  color: colors.surface,
  fontSize: 16,
  fontWeight: "bold",
}

// Expanded content styles
const $expandedContent: ViewStyle = {
  paddingTop: spacing.lg,
  paddingHorizontal: spacing.md,
}

const $addressSection: ViewStyle = {
  marginBottom: spacing.lg,
}

const $addressTitle: TextStyle = {
  fontWeight: "600",
  color: colors.text,
  marginBottom: spacing.sm,
  fontSize: 11,
  letterSpacing: 1,
  textTransform: "uppercase",
  opacity: 0.5,
}

const $addressContainer: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 16,
  padding: spacing.md,
  // @ts-ignore - Web-specific
  boxShadow: "0 2px 12px rgba(17, 30, 44, 0.06)",
  borderWidth: 1,
  borderColor: "rgba(0, 0, 0, 0.04)",
}

const $addressRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  gap: spacing.sm,
}

const $addressText: TextStyle = {
  color: colors.text,
  fontSize: 15,
  fontWeight: "600",
  flex: 1,
  lineHeight: 22,
}

const $openingHoursRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  gap: spacing.sm,
  marginTop: spacing.xs,
}

const $openingHoursText: TextStyle = {
  color: colors.textDim,
  fontSize: 13,
  fontWeight: "500",
  flex: 1,
  lineHeight: 20,
}

const $mapsSeparator: ViewStyle = {
  height: 1,
  backgroundColor: "rgba(0, 0, 0, 0.06)",
  marginTop: spacing.sm,
  marginBottom: spacing.xs,
}

const $mapsLinkRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: spacing.xs,
}

const $mapsLink: TextStyle = {
  fontSize: 14,
  color: colors.primary,
  fontWeight: "600",
}

// Mobile layout styles
const $mobileCenteredWrapper: ViewStyle = {
  flex: 1,
  justifyContent: "center", // Center vertically in available space (collapsed only)
  alignItems: "center",
}

const $mobileExpandedWrapper: ViewStyle = {
  paddingTop: spacing.md, // Normal top spacing when expanded
}

const $mobileMainRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingHorizontal: spacing.sm,
  paddingTop: spacing.md, // 16px padding top
  gap: spacing.md,
  width: "100%", // Take full width within centered wrapper
}

const $mobileCarContainer: ViewStyle = {
  flex: 0.4,
  alignItems: "flex-start",
}

const $mobileTimeCardsColumn: ViewStyle = {
  flex: 0.4,
  gap: spacing.xs,
}

// const $mobileInspectionCard: ViewStyle = {
//   backgroundColor: colors.background, // Same blue as time cards
//   borderRadius: 12,
//   paddingVertical: spacing.xs,
//   paddingHorizontal: spacing.xs,
//   alignItems: "center",
//   justifyContent: "center",
//   height: 70, // Same as mobile time cards
//   minWidth: 85, // Ensure minimum width for content
//   position: "relative", // Needed for absolute positioning of status indicator
//   // @ts-ignore - Web-specific
//   boxShadow: `0 1px 3px rgba(17, 30, 44, 0.08)`,
//   elevation: 2,
// }

// const $mobileStatusIndicator: ViewStyle = {
//   position: "absolute",
//   top: spacing.xs,
//   right: spacing.xs,
//   zIndex: 1,
// }

const $mobileInspectionIconContainer: ViewStyle = {
  marginVertical: 2,
}

// const $mobileTimeCardsRow: ViewStyle = {
//   flexDirection: "row",
//   justifyContent: "space-between",
//   paddingHorizontal: spacing.xs,
//   gap: spacing.xs,
// }

const $mobileTimeCard: ViewStyle = {
  backgroundColor: colors.background,
  borderRadius: 14,
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.md,
  paddingLeft: (spacing.md as number) + 2,
  alignItems: "flex-start",
  justifyContent: "center",
  minHeight: 64,
  position: "relative",
  borderLeftWidth: 3,
  borderLeftColor: colors.primary,
  // @ts-ignore - Web-specific
  boxShadow: "0 2px 8px rgba(17, 30, 44, 0.12), inset 0 1px 0 rgba(255,255,255,0.04)",
  // @ts-ignore - Web-specific
  backgroundImage: `linear-gradient(135deg, ${colors.surfaceDarkTop} 0%, ${colors.surfaceDarkBottom} 100%)`,
}

const $mobileTimeCardLabel: TextStyle = {
  color: colors.surface,
  fontSize: 9,
  marginBottom: 2,
  opacity: 0.6,
  fontWeight: "600",
  letterSpacing: 1,
  textTransform: "uppercase",
}

const $mobileTimeCardTime: TextStyle = {
  color: colors.surface,
  fontSize: 18,
  fontWeight: "700",
  letterSpacing: -0.5,
  lineHeight: 22,
}

// Keep this for the expanded Prüfbericht card
const $mobileTimeCardDate: TextStyle = {
  color: colors.surface,
  fontSize: 10,
  opacity: 0.5,
  fontWeight: "500",
  marginTop: 1,
}

// Expanded Prüfbericht card (mobile only, shown when expanded)
const $mobileExpandedPrufberichtContainer: ViewStyle = {
  alignItems: "stretch",
  marginBottom: spacing.md,
}

const $mobileFullWidthInspectionCard: ViewStyle = {
  backgroundColor: colors.background,
  borderRadius: 16,
  paddingVertical: spacing.md,
  paddingHorizontal: spacing.md,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  minHeight: 80,
  // @ts-ignore - Web-specific
  boxShadow: `0 3px 6px rgba(17, 30, 44, 0.15)`,
  elevation: 4,
}

const $mobileInspectionLeftSide: ViewStyle = {
  flex: 1,
  alignItems: "flex-start",
}

const $mobileInspectionRightSide: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.sm,
}

const $mobileInspectionLabel: TextStyle = {
  color: colors.surface,
  fontSize: 14,
  fontWeight: "600",
  marginBottom: spacing.xs,
}

const $mobileInspectionStatus: TextStyle = {
  color: colors.surface,
  fontSize: 12,
  opacity: 0.9,
  fontWeight: "500",
}

const $mobileInlineStatusIndicator: ViewStyle = {
  // No absolute positioning needed
}

// Missing disabled state for mobile
const $inspectionButtonDisabled: ViewStyle = {
  backgroundColor: colors.palette.neutral400,
  opacity: 0.7,
}

// Help Card Styles
const $helpCard: ViewStyle = {
  width: "100%",
  backgroundColor: colors.palette.neutral100,
  borderRadius: 16,
  overflow: "hidden",
  borderWidth: 1,
  borderColor: colors.palette.neutral200,
}

const $helpCardHeader: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  padding: spacing.md,
  paddingBottom: spacing.sm,
  cursor: "pointer" as any,
}

const $helpCardTitle: TextStyle = {
  flex: 1,
  fontSize: 16,
  fontWeight: "600",
  color: colors.text,
  marginRight: spacing.sm,
}

const $helpCardContent: ViewStyle = {
  paddingHorizontal: spacing.md,
  paddingBottom: spacing.md,
}

const $helpCardText: TextStyle = {
  fontSize: 14,
  color: colors.text,
  lineHeight: 22,
}

const $helpCardLink: TextStyle = {
  color: colors.primary,
  fontWeight: "600",
  textDecorationLine: "underline",
}

const $buttonRow: ViewStyle = {
  flexDirection: "row",
  gap: spacing.sm,
  marginTop: spacing.md,
}

const $whatsappButton: ViewStyle = {
  backgroundColor: colors.whatsAppGreen,
  borderRadius: 8,
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.md,
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "center",
  // @ts-ignore - Web-specific
  boxShadow: "0 2px 3px rgba(0, 0, 0, 0.1)",
  elevation: 2,
  cursor: "pointer" as any,
  flex: 1,
}

const $whatsappButtonText: TextStyle = {
  color: colors.surface,
  fontSize: 14,
  fontWeight: "600",
}

// const $rescheduleButton: ViewStyle = {
//   backgroundColor: colors.primary,
//   borderRadius: 12,
//   paddingVertical: spacing.md,
//   paddingHorizontal: spacing.lg,
//   marginTop: spacing.md,
//   marginLeft: "auto",
//   marginRight: spacing.sm,
//   alignSelf: "flex-end",
//   // @ts-ignore - Web-specific
//   boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
//   elevation: 3,
// }

// const $rescheduleButtonContent: ViewStyle = {
//   flexDirection: "row",
//   alignItems: "center",
//   justifyContent: "center",
//   gap: spacing.sm,
// }

const $rescheduleButtonInRow: ViewStyle = {
  backgroundColor: colors.primary,
  borderRadius: 8,
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.md,
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "center",
  // @ts-ignore - Web-specific
  boxShadow: "0 2px 3px rgba(0, 0, 0, 0.1)",
  elevation: 2,
  cursor: "pointer" as any,
  flex: 1,
  gap: spacing.xs,
}

const $rescheduleButtonText: TextStyle = {
  color: colors.surface,
  fontSize: 14,
  fontWeight: "600",
  textAlign: "center",
}

const $cancelButtonInRow: ViewStyle = {
  backgroundColor: colors.primary, // collapsed to primary (one-red rule)
  borderRadius: 8,
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.md,
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "center",
  // @ts-ignore - Web-specific
  boxShadow: "0 2px 3px rgba(0, 0, 0, 0.1)",
  elevation: 2,
  cursor: "pointer" as any,
  flex: 1,
  gap: spacing.xs,
}

const $cancelButtonText: TextStyle = {
  color: colors.surface,
  fontSize: 14,
  fontWeight: "600",
  textAlign: "center",
}

// Navigation Popup Styles (Driveby Phase 1)
const $navPopupCard: ViewStyle = {
  borderRadius: 16,
  overflow: "hidden",
  // @ts-ignore - Web-specific
  boxShadow: "0 12px 32px rgba(17, 30, 44, 0.2), 0 2px 6px rgba(17, 30, 44, 0.08)",
  elevation: 10,
}

const $navPopupHeader: ViewStyle = {
  backgroundColor: colors.primary,
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.md,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
}

const $navPopupHeaderContent: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
  flex: 1,
}

const $navPopupHeaderTitle: TextStyle = {
  color: colors.surface,
  fontSize: 15,
  fontWeight: "700",
  letterSpacing: 0.2,
}

const $navPopupCloseButton: ViewStyle = {
  width: 26,
  height: 26,
  borderRadius: 13,
  backgroundColor: "rgba(255, 255, 255, 0.15)",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer" as any,
}

const $navPopupBody: ViewStyle = {
  backgroundColor: colors.surface,
  padding: spacing.md,
}

const $navPopupHubRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  gap: spacing.sm,
  marginBottom: spacing.sm,
}

const $navPopupHubDot: ViewStyle = {
  width: 10,
  height: 10,
  borderRadius: 5,
  backgroundColor: colors.primary,
  marginTop: 4,
  // @ts-ignore - Web-specific
  boxShadow: "0 0 0 3px rgba(255, 56, 47, 0.15)",
}

const $navPopupHubName: TextStyle = {
  color: colors.text,
  fontSize: 15,
  fontWeight: "700",
  marginBottom: 2,
}

const $navPopupAddress: TextStyle = {
  color: colors.textDim,
  fontSize: 13,
  fontWeight: "400",
  lineHeight: 18,
}

const $navPopupNoteBox: ViewStyle = {
  backgroundColor: "rgba(255, 56, 47, 0.05)",
  borderRadius: 10,
  padding: spacing.sm,
  flexDirection: "row",
  alignItems: "flex-start",
  gap: spacing.xs,
  marginBottom: spacing.sm,
  borderWidth: 1,
  borderColor: "rgba(255, 56, 47, 0.1)",
}

const $navPopupNote: TextStyle = {
  color: colors.text,
  fontSize: 12,
  fontWeight: "500",
  lineHeight: 17,
  flex: 1,
}

const $navPopupButton: ViewStyle = {
  backgroundColor: colors.primary,
  borderRadius: 10,
  paddingVertical: 11,
  paddingHorizontal: spacing.md,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.xs,
  // @ts-ignore - Web-specific
  boxShadow: "0 2px 8px rgba(255, 56, 47, 0.3)",
  elevation: 3,
  cursor: "pointer" as any,
  // @ts-ignore - Web-specific
  transition: "transform 0.15s ease, box-shadow 0.15s ease",
}

const $navPopupButtonText: TextStyle = {
  color: colors.surface,
  fontSize: 14,
  fontWeight: "700",
  letterSpacing: 0.3,
  flex: 1,
  textAlign: "center",
}

const $phaseLabelRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
}
